import { css } from '@emotion/react';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  ModalControlModalProps,
  Modal,
  Text,
  Heading,
  TextareaField,
  useFormField,
  TextLink,
  EmailField,
  CopyToClipboardButton,
  useAlert,
} from '@frontend/design-system';

export const WebToTextInstallationModal = ({
  businessId,
  locationId,
  orgId,
  ...modalProps
}: ModalControlModalProps & { businessId?: string; locationId: string; orgId: string }) => {
  const { t } = useTranslation('ai-receptionist-settings');

  return (
    <Modal {...modalProps} maxWidth={800}>
      <Modal.Header onClose={modalProps.onClose}>{t('Install Web to Text')}</Modal.Header>
      <Modal.Body>
        <CodeSnippet businessId={businessId} />
        <WordPressInstructions />
        <VideoInstructions />
        <SendToYourDeveloper locationId={locationId} orgId={orgId} />
        <TextLink to='mailto:support@truelark.com' weight='bold'>
          {t('Contact Support')}
        </TextLink>
      </Modal.Body>
    </Modal>
  );
};

const CodeSnippet = ({ businessId }: { businessId?: string }) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const textAreaProps = useFormField({
    value: `<script src='https://truelark.com/dental-chat-widget/js/config.js'></script>         
      <script src='https://truelark.com/dental-chat-widget/js/loader.js'></script>         
      <script> fdchat({clientId: '${businessId}'}); </script>`,
    type: 'text',
  });

  return (
    <Text as='div' css={styles.container}>
      <Heading level={3}>{t('Copy Code Snippet')}</Heading>
      <Text>
        {t(
          "Copy and paste the code snippet below on one or many pages on your website, where you'd like the AI Receptionist Web to Text to appear."
        )}
      </Text>
      <Text>
        {t(
          "Once you've completed your changes, open your website. Your web chat should appear in the bottom right corner."
        )}
      </Text>
      <div css={styles.row}>
        <TextareaField
          readOnly
          containerCss={css`
            flex: 1;
          `}
          {...textAreaProps}
          label=''
          name='installation-code-snippet'
        />
        <CopyToClipboardButton
          textToCopy={textAreaProps.value}
          hasSuccessTooltip={true}
          tooltipText={t('Copy code snippet')}
          tooltipSuccessText={t('Code snippet copied!')}
          hasIcon
        >
          {t('Copy')}
        </CopyToClipboardButton>
      </div>
    </Text>
  );
};

const WordPressInstructions = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <>
      <Heading level={3}>{t('WordPress Instructions')}</Heading>
      <Text as='div'>
        <Trans t={t}>
          Click{' '}
          <TextLink
            href='https://www.notion.so/Dental-Web-Chat-Installation-instruction-Wordpress-8a519d0651be46d6879a4d68db72d1e7'
            target='_blank'
          >
            here
          </TextLink>{' '}
          for WordPress instructions.
        </Trans>
      </Text>
    </>
  );
};

const VideoInstructions = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <>
      <Heading level={3}>{t('Video')}</Heading>
      <Text as='div'>
        <Trans t={t}>
          Watch this{' '}
          <TextLink
            href='https://truelark.notion.site/Webchat-and-Web-to-Text-Support-Instructions-32d4d4dc020a80598d99fa3cf698c1c3'
            target='_blank'
          >
            video
          </TextLink>{' '}
          for more details!
        </Trans>
      </Text>
    </>
  );
};

const SendToYourDeveloper = ({ locationId, orgId }: { locationId: string; orgId: string }) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const emailProps = useFormField({
    value: '',
    type: 'email',
  });

  const alert = useAlert();

  const { mutate: sendEmail } = AiSettingsV1.Mutations.useSendWebChatSettingsInstructionEmailMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Email sent successfully'));
        emailProps.onChange({ name: 'email', value: '' });
      },
      onError: () => {
        alert.error(t('Failed to send email'));
      },
    },
  });

  const handleSendEmail = () =>
    sendEmail({
      email: emailProps.value,
      locationId: locationId,
      orgId: orgId,
    });

  return (
    <Text as='div' css={styles.container}>
      <Heading level={3}>{t('Send to Your Developer')}</Heading>
      <Text>{t('Send instructions to your developer.')}</Text>
      <div css={styles.row}>
        <EmailField
          containerCss={css`
            flex: 1;
          `}
          label={t('Email Address')}
          name='email-address'
          {...emailProps}
        />
        <Button
          iconName='send'
          variant='secondary'
          onClick={handleSendEmail}
          size='large'
          disabled={!emailProps.value || !!emailProps.error}
        >
          {t('Send')}
        </Button>
      </div>
    </Text>
  );
};

const styles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  }),
  row: css({
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
  }),
};
